import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '@/hooks/UserContext';
import { apiGetRequest, apiPostRequest } from '@/utils/apiClient';
import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import { Button } from '@/components/Button';
import { useTranslation } from 'react-i18next';
import { usePanelLoading } from '@/components/PanelLoadingBoundary';

const ALLOWED_TAGS: string[] = ['h1', 'p', 'br', 'strong', 'em', 'u'];
const ALLOWED_ATTR: string[] = ['class'];
const FORBID_TAGS: string[] = ['script', 'object', 'embed', 'form', 'input', 'iframe'];
const FORBID_ATTR: string[] = ['onerror', 'onload', 'onclick', 'onmouseover', 'style'];

const SANITIZE_OPTIONS: Config = {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS,
    FORBID_ATTR,
};

const normalizeEditableHtml = (rawHtml: string): string => {
    if (!rawHtml) return '';
    return rawHtml
        .replace(/<div>(\s|&nbsp;|<br\s*\/?>)*<\/div>/gi, '<p><br></p>')
        .replace(/<div\b[^>]*>/gi, '<p>')
        .replace(/<\/div>/gi, '</p>')
        .replace(/<p>(\s|&nbsp;)*<\/p>/gi, '<p><br></p>');
};

const sanitizeMemoHtml = (rawHtml: string): string => {
    if (!rawHtml) return '';
    const normalized = normalizeEditableHtml(rawHtml);
    return DOMPurify.sanitize(normalized, SANITIZE_OPTIONS) as string;
};

type MemoProps = {
    onEnterFullScreen?: () => void;
    onExitFullScreen?: () => void;
    isFullScreen?: boolean;
};

const Memo: React.FC<MemoProps> = ({ isFullScreen = false }) => {
    const { userInfo, isLoading } = useUser();
    const { t } = useTranslation('memo');
    const editorRef = useRef<HTMLDivElement>(null);
    const [html, setHtml] = useState<string>('');
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
    const [shouldUpdateDOM, setShouldUpdateDOM] = useState<boolean>(false);
    const [isEmpty, setIsEmpty] = useState<boolean>(true);
    const [plainTextLength, setPlainTextLength] = useState<number>(0);
    const { setLoading } = usePanelLoading();
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const prevIsFullScreenRef = useRef<boolean>(false);
    const placeholderItemsRaw = t('panel.placeholder.items', {
        returnObjects: true,
    });
    const placeholderItems = Array.isArray(placeholderItemsRaw)
        ? (placeholderItemsRaw as string[])
        : [];

    // テキストが空かどうかをチェックする関数
    const checkIfEmpty = (htmlContent: string) => {
        if (!htmlContent) return true;
        // HTMLタグを除去してテキストのみを取得
        const textContent = htmlContent.replace(/<[^>]*>/g, '').trim();
        // <br>のみの場合も空とみなす
        const hasOnlyBr = /^(<br\s*\/?>)*$/i.test(htmlContent.trim());
        return textContent === '' || hasOnlyBr;
    };

    // プレーンテキストの文字数を取得する関数
    const getPlainTextLength = (htmlContent: string) => {
        if (!htmlContent) return 0;
        // HTMLタグを除去
        const textContent = htmlContent.replace(/<[^>]*>/g, '');
        // HTMLエンティティをデコード
        const decodedText = textContent
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, ' ');
        return decodedText.trim().length;
    };

    // メモデータを取得する関数
    const fetchMemo = async () => {
        if (!userInfo) return;
        setIsDataLoaded(false);
        try {
            const res = await apiGetRequest('/api/memo');
            if (res.ok) {
                const data = await res.json();
                const memoContent = data.memo || '';
                const sanitized = sanitizeMemoHtml(memoContent);
                setHtml(sanitized);
                setIsEmpty(checkIfEmpty(sanitized));
                setPlainTextLength(getPlainTextLength(sanitized));
                setIsDataLoaded(true);
                setShouldUpdateDOM(true); // 初回読み込み時はDOM更新を許可
                return sanitized;
            }
        } catch (e) {
            console.error('メモ取得エラー:', e);
        }
        setIsDataLoaded(true);
        return '';
    };

    // userInfoが利用可能になったらメモデータを取得
    useEffect(() => {
        if (!isLoading && userInfo) {
            fetchMemo();
        }
    }, [userInfo, isLoading]);

    // フルスクリーン解除時にデータを再取得
    useEffect(() => {
        // フルスクリーンがtrueからfalseに変わった時だけ再取得
        if (prevIsFullScreenRef.current && !isFullScreen && userInfo && !isLoading) {
            fetchMemo();
        }
        prevIsFullScreenRef.current = isFullScreen;
    }, [isFullScreen, userInfo, isLoading]);

    useEffect(() => {
        if (!userInfo || isLoading) {
            setLoading(true);
            return;
        }
        setLoading(!isDataLoaded);
    }, [isDataLoaded, isLoading, setLoading, userInfo]);

    // 初回読み込み時またはshouldUpdateDOMがtrueの時のみDOM更新
    useEffect(() => {
        if (isDataLoaded && editorRef.current && shouldUpdateDOM) {
            editorRef.current.innerHTML = html;
            setShouldUpdateDOM(false); // DOM更新後はフラグをリセット
        }
    }, [html, isDataLoaded, shouldUpdateDOM]);

    // テキストが入力されたら、htmlを更新（DOM更新は行わない）
    const handleInput = () => {
        if (editorRef.current) {
            const newHtml = editorRef.current.innerHTML;
            const sanitized = sanitizeMemoHtml(newHtml);
            if (sanitized !== html) {
                setHtml(sanitized);
            }
            setIsEmpty(checkIfEmpty(sanitized));
            setPlainTextLength(getPlainTextLength(sanitized));
            setIsDirty(true);
        }
    };

    // フォーカス時の処理
    const handleFocus = () => {
        setIsFocused(true);
    };

    // フォーカスアウト時の処理
    const handleBlur = () => {
        setIsFocused(false);
        if (editorRef.current) {
            const currentHtml = editorRef.current.innerHTML;
            const sanitized = sanitizeMemoHtml(currentHtml);
            if (sanitized !== html) {
                setHtml(sanitized);
            }
            setIsEmpty(checkIfEmpty(sanitized));
            setPlainTextLength(getPlainTextLength(sanitized));
            setShouldUpdateDOM(true);
        }
    };

    // キーダウンイベントを処理
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;

            const range = selection.getRangeAt(0);
            const currentElement = range.startContainer;

            // 現在のカーソル位置がH1要素内にあるかチェック
            let h1Element: HTMLElement | null = null;
            if (currentElement.nodeType === Node.TEXT_NODE) {
                h1Element = currentElement.parentElement?.closest('h1') || null;
            } else if (currentElement.nodeType === Node.ELEMENT_NODE) {
                const element = currentElement as HTMLElement;
                h1Element = element.tagName === 'H1' ? element : element.closest('h1');
            }

            if (h1Element) {
                e.preventDefault();

                // H1要素内でEnterが押された場合の処理
                const h1Range = document.createRange();
                h1Range.selectNodeContents(h1Element);

                // カーソル位置がH1の最後にあるかチェック
                const isAtEnd =
                    range.endOffset === h1Range.endOffset &&
                    range.endContainer === h1Range.endContainer;

                if (isAtEnd) {
                    // H1の最後で改行 → 新しいp要素を作成
                    const pElement = document.createElement('p');
                    pElement.innerHTML = '<br>'; // 空の段落にはbrが必要
                    pElement.className = 'leading-loose tracking-[0.1em]';

                    // H1要素の後に挿入
                    h1Element.parentNode?.insertBefore(pElement, h1Element.nextSibling);

                    // カーソルを新しいp要素に移動
                    const newRange = document.createRange();
                    newRange.setStart(pElement, 0);
                    newRange.setEnd(pElement, 0);
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                } else {
                    // H1の途中で改行 → H1を分割
                    const afterText = range.extractContents();

                    // 新しいp要素を作成して、切り取った内容を挿入
                    const pElement = document.createElement('p');
                    pElement.className = 'leading-loose tracking-[0.1em]';
                    pElement.appendChild(afterText);

                    // H1要素の後に挿入
                    h1Element.parentNode?.insertBefore(pElement, h1Element.nextSibling);

                    // カーソルを新しいp要素の先頭に移動
                    const newRange = document.createRange();
                    newRange.setStart(pElement.firstChild || pElement, 0);
                    newRange.setEnd(pElement.firstChild || pElement, 0);
                    selection.removeAllRanges();
                    selection.addRange(newRange);
                }

                // 変更をstateに反映
                handleInput();
            }
        }
    };

    // H1タグを適用する関数
    const applyH1 = () => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const editor = editorRef.current;
        const anchorNode = selection.anchorNode;
        if (!anchorNode || !editor.contains(anchorNode)) {
            editor.focus();
            return;
        }

        const range = selection.getRangeAt(0);
        const currentElement = range.startContainer;

        // 現在の選択範囲がH1要素内にあるかチェック
        let h1Element: HTMLElement | null = null;
        if (currentElement.nodeType === Node.TEXT_NODE) {
            h1Element = currentElement.parentElement?.closest('h1') || null;
        } else if (currentElement.nodeType === Node.ELEMENT_NODE) {
            const element = currentElement as HTMLElement;
            h1Element = element.tagName === 'H1' ? element : element.closest('h1');
        }

        if (h1Element) {
            // H1要素内にカーソルがある場合 → H1を解除してp要素に変換
            const h1Text = h1Element.textContent || '';

            // 新しいp要素を作成
            const pElement = document.createElement('p');
            pElement.textContent = h1Text;
            pElement.className = 'leading-loose tracking-[0.1em]';

            // H1要素をp要素に置き換え
            h1Element.parentNode?.replaceChild(pElement, h1Element);

            // カーソル位置を復元（p要素内の同じ位置に配置）
            const newRange = document.createRange();
            const textNode = pElement.firstChild;
            if (textNode) {
                // 元のH1内でのカーソル位置を計算
                const h1Range = document.createRange();
                h1Range.selectNodeContents(h1Element);
                const originalOffset = Math.min(range.startOffset, h1Text.length);

                newRange.setStart(textNode, originalOffset);
                newRange.setEnd(textNode, originalOffset);
                selection.removeAllRanges();
                selection.addRange(newRange);
            }
        } else {
            // H1要素外での通常のH1適用処理
            const selectedText = range.toString();

            if (selectedText) {
                // 選択されたテキストがある場合、H1タグで囲む
                const h1Element = document.createElement('h1');
                h1Element.textContent = selectedText;
                h1Element.className = 'text-xl tracking-[0.1em] leading-relaxed my-2';

                try {
                    range.deleteContents();
                    range.insertNode(h1Element);

                    // カーソルをH1要素の後に移動
                    range.setStartAfter(h1Element);
                    range.setEndAfter(h1Element);
                    selection.removeAllRanges();
                    selection.addRange(range);
                } catch (e) {
                    console.error('H1適用エラー:', e);
                }
            } else {
                // 選択されたテキストがない場合、新しいH1要素を挿入
                const h1Element = document.createElement('h1');
                h1Element.className = 'text-xl tracking-[0.1em] leading-relaxed mt-2';
                h1Element.textContent = t('panel.defaultHeading');

                try {
                    range.insertNode(h1Element);

                    // H1要素内のテキストを選択状態にする
                    range.selectNodeContents(h1Element);
                    selection.removeAllRanges();
                    selection.addRange(range);
                } catch (e) {
                    console.error('H1挿入エラー:', e);
                }
            }
        }

        handleInput();
    };

    // メモを保存する関数
    const handleSave = async () => {
        if (!userInfo) return;
        try {
            const sanitized = sanitizeMemoHtml(html);
            const res = await apiPostRequest('/api/memo/register', {
                userId: userInfo.userId,
                memo: sanitized,
            });
            if (res.ok) {
                setIsDirty(false);
                if (sanitized !== html) {
                    setHtml(sanitized);
                }
                setShouldUpdateDOM(true);
            }
        } catch (e) {
            console.error('メモ保存エラー:', e);
            alert(t('panel.saveError'));
        }
    };

    // UserContextまたはメモデータの読み込み中
    if (isLoading || !isDataLoaded) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                {t('state.loading')}
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col min-h-0">
            <div className="mb-2 flex gap-2 justify-between items-center">
                <div className="flex gap-2 items-center">
                    <Button
                        onClick={applyH1}
                        className="h-8 w-16 text-xs text-gray-500 bg-white border-none hover:bg-gray-50"
                    >
                        {t('panel.headingButton')}
                    </Button>
                    {isDirty && (
                        <div className="text-xs text-gray-500 ps-2">
                            <span
                                className={
                                    plainTextLength > 1400
                                        ? 'text-orange-500 font-medium'
                                        : plainTextLength > 1500
                                          ? 'text-red-500 font-bold'
                                          : ''
                                }
                            >
                                {plainTextLength}
                            </span>
                            <span className="mx-1">/</span>
                            <span>{t('panel.limit')}</span>
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    {isDirty && (
                        <Button
                            variant="original"
                            size="sm"
                            onClick={handleSave}
                            className="h-8 w-16"
                        >
                            {t('panel.saveButton')}
                        </Button>
                    )}
                </div>
            </div>
            {plainTextLength > 1500 && (
                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                    {t('panel.overLimit', { over: plainTextLength - 1500 })}
                </div>
            )}
            <div className="relative flex-1 w-full min-h-0 overflow-hidden">
                {isEmpty && !isFocused && (
                    <div className="pointer-events-none absolute inset-0 text-gray-400 tracking-[0.1em] leading-loose whitespace-pre-line p-2">
                        <p>{t('panel.placeholder.intro')}</p>
                        <h1 className="text-xl tracking-[0.1em] leading-relaxed my-2 text-gray-400">
                            {t('panel.placeholder.heading')}
                        </h1>
                        <ul className="space-y-1 text-gray-400">
                            {placeholderItems.map((item, index) => (
                                <li key={index}>・{item}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div
                    ref={editorRef}
                    className="memo-editor flex-1 w-full h-full p-2 text-gray-800 border border-white overflow-auto tracking-[0.1em] leading-loose focus:outline-none focus:border focus:border-gray-200 focus:rounded-lg"
                    contentEditable
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    suppressContentEditableWarning={true}
                    style={{
                        position: 'relative',
                        height: '100%',
                    }}
                />
            </div>
        </div>
    );
};

export default Memo;
